'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRealtimeRun } from '@trigger.dev/react-hooks';
import {
  ArrowsOut,
  ArrowCounterClockwise,
  Download,
  Gauge,
  Pause,
  Play,
  SpeakerHigh,
  SpeakerSlash,
  Spinner,
  X,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  buildHyperframesPreviewDocument,
  hyperframesGainDbToVolume,
  injectHyperframesAudioTracks,
  injectVideoRuntime,
  type HyperframesAudioTrackInput,
} from '@/lib/hyperframes-document';
import { useProductTelemetryOnce } from '@/hooks/use-product-telemetry-once';
import { captureProductTelemetry } from '@/lib/product-telemetry';
import { emitAutoSystemMessage } from '@/lib/auto-system-message-bus';
import { hyperframesExportRunToView, type RenderJobView } from '@/lib/render-job-view';
import { RenderJobCard } from '@/components/render-job-card';
import type { renderHyperframesExportTask } from '@/trigger/hyperframes-render';

const SPEED_OPTIONS = [0.25, 0.5, 1, 1.5, 2] as const;

function formatTime(seconds: number) {
  const safeSeconds = Math.max(0, seconds);
  const mins = Math.floor(safeSeconds / 60);
  const secs = safeSeconds - mins * 60;
  if (mins === 0) return `${secs.toFixed(1)}s`;
  return `${mins}:${String(Math.floor(secs)).padStart(2, '0')}`;
}

interface HyperframesRendererProps {
  title: string;
  description?: string;
  htmlDocument: string;
  durationSeconds: number;
  fps: number;
  width: number;
  height: number;
  variables?: Record<string, unknown>;
  audioTracks?: HyperframesAudioTrackInput[];
  visualizationId?: string;
  displayContext?: 'inline' | 'artifact';
  renderActions?: React.ReactNode;
}

/**
 * The MP4 export runs as a durable trigger.dev task now
 * (trigger/hyperframes-render.ts). POST /api/render/hyperframes returns
 * { taskId, publicAccessToken } and we subscribe via trigger realtime —
 * same pattern as PendingNarratedVideo. 'tracking' hands off to the
 * realtime run for the rest of the lifecycle.
 */
type ExportState =
  | { status: 'idle' }
  | { status: 'starting' }
  | { status: 'tracking'; taskId: string; publicAccessToken: string }
  | { status: 'error'; message: string };

export function HyperframesRenderer({
  title,
  description,
  htmlDocument,
  durationSeconds,
  fps,
  width,
  height,
  variables,
  audioTracks,
  visualizationId,
  displayContext = 'inline',
  renderActions,
}: HyperframesRendererProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const animationRef = useRef<number | null>(null);
  const lastTickRef = useRef<number | null>(null);
  const timeRef = useRef(0);
  const readyRef = useRef(false);
  const renderStartRef = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const hasTrackedReadyRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Poster-first: the preview loads PAUSED at frame 0 — the live composition
  // itself is the poster frame — and only starts on an explicit play gesture.
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const [exportState, setExportState] = useState<ExportState>({ status: 'idle' });
  // Audio starts muted: browser autoplay policy blocks unmuted playback until
  // the user interacts, so the speaker toggle is the unlock gesture.
  const [audioEnabled, setAudioEnabled] = useState(false);
  const audioEnabledRef = useRef(false);
  const audioElsRef = useRef<Array<{ el: HTMLAudioElement; track: HyperframesAudioTrackInput }>>([]);

  useProductTelemetryOnce(
    'visualization_render_failed',
    Boolean(runtimeError),
    {
      tool_name: 'hyperframes',
      failure_kind: 'iframe-runtime-error',
      visualization_id: visualizationId,
      display_context: displayContext,
    },
    runtimeError ? `${visualizationId ?? 'hyperframes'}:${runtimeError}` : undefined,
  );

  // Saved visualizations already carry injected <audio> elements and the gx
  // motion runtime; the inline chat path receives the raw tool input, so
  // inject here too (both injections are idempotent).
  const effectiveHtmlDocument = useMemo(
    () => injectVideoRuntime(injectHyperframesAudioTracks(htmlDocument, audioTracks ?? [])),
    [audioTracks, htmlDocument],
  );

  const previewDocument = useMemo(
    () => buildHyperframesPreviewDocument({
      htmlDocument: effectiveHtmlDocument,
      width,
      height,
      durationSeconds,
      variables,
    }),
    [durationSeconds, effectiveHtmlDocument, height, variables, width],
  );

  // Parent-side audio playback for audioTracks, synced to the preview clock.
  // (The iframe's injected <audio> copies stay paused — the preview controller
  // only seeks media, it never plays it — so there is no double playback.)
  useEffect(() => {
    const tracks = audioTracks ?? [];
    audioElsRef.current = tracks.map((track) => {
      const el = new Audio();
      el.src = track.url;
      el.preload = 'auto';
      el.crossOrigin = 'anonymous';
      el.volume = hyperframesGainDbToVolume(track.gainDb);
      return { el, track };
    });

    return () => {
      for (const { el } of audioElsRef.current) {
        el.pause();
        el.removeAttribute('src');
        el.load();
      }
      audioElsRef.current = [];
    };
  }, [audioTracks]);

  const syncAudio = useCallback((time: number, playing: boolean) => {
    for (const { el, track } of audioElsRef.current) {
      const local = time - track.startSec;
      const trackDuration = track.durationSec
        ?? (Number.isFinite(el.duration) ? el.duration : Number.POSITIVE_INFINITY);
      const active = local >= 0 && local < trackDuration;
      const shouldPlay = active && playing && audioEnabledRef.current;

      if (active && Math.abs(el.currentTime - local) > 0.25) {
        try {
          el.currentTime = Math.max(0, local);
        } catch {
          // Not seekable yet; the next sync will retry.
        }
      }

      if (shouldPlay) {
        if (el.paused) el.play().catch(() => undefined);
      } else if (!el.paused) {
        el.pause();
      }
    }
  }, []);

  const postTime = useCallback((time: number, paused = !isPlaying) => {
    iframeRef.current?.contentWindow?.postMessage({
      type: 'galexia:hyperframes:setTime',
      time,
      paused,
    }, '*');
  }, [isPlaying]);

  useEffect(() => {
    readyRef.current = false;
    hasTrackedReadyRef.current = false;
    renderStartRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
    timeRef.current = 0;
    setCurrentTime(0);
    setRuntimeError(null);
    // A new document (e.g. a surgical edit) returns to the poster state.
    setHasStarted(false);
    setIsPlaying(false);
  }, [previewDocument]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type === 'galexia:hyperframes:ready') {
        readyRef.current = true;
        postTime(timeRef.current, !isPlaying);

        if (!hasTrackedReadyRef.current) {
          hasTrackedReadyRef.current = true;
          captureProductTelemetry('visualization_render_ready', {
            tool_name: 'hyperframes',
            visualization_id: visualizationId,
            display_context: displayContext,
            render_duration_ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - renderStartRef.current),
            composition_width: width,
            composition_height: height,
            duration_seconds: durationSeconds,
            fps,
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [displayContext, durationSeconds, fps, height, isPlaying, postTime, visualizationId, width]);

  useEffect(() => {
    const tick = (timestamp: number) => {
      if (lastTickRef.current === null) {
        lastTickRef.current = timestamp;
      }

      const elapsed = (timestamp - lastTickRef.current) / 1000;
      lastTickRef.current = timestamp;

      if (isPlaying && readyRef.current) {
        const next = (timeRef.current + elapsed * speed) % durationSeconds;
        timeRef.current = next;
        setCurrentTime(next);
        postTime(next, false);
        syncAudio(next, true);
      }

      animationRef.current = requestAnimationFrame(tick);
    };

    animationRef.current = requestAnimationFrame(tick);

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = null;
      lastTickRef.current = null;
    };
  }, [durationSeconds, isPlaying, postTime, speed, syncAudio]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const handleSeek = useCallback((value: number) => {
    const next = Math.max(0, Math.min(durationSeconds, value));
    // Scrubbing counts as engaging the player — drop the poster scrim so the
    // user sees the frame they're seeking to.
    setHasStarted(true);
    timeRef.current = next;
    setCurrentTime(next);
    postTime(next, !isPlaying);
    syncAudio(next, isPlaying);
  }, [durationSeconds, isPlaying, postTime, syncAudio]);

  const handleTogglePlay = useCallback(() => {
    const nextPlaying = !isPlaying;
    setHasStarted(true);
    setIsPlaying(nextPlaying);
    postTime(timeRef.current, !nextPlaying);
    syncAudio(timeRef.current, nextPlaying);
  }, [isPlaying, postTime, syncAudio]);

  const handleRestart = useCallback(() => {
    setHasStarted(true);
    timeRef.current = 0;
    setCurrentTime(0);
    postTime(0, !isPlaying);
    syncAudio(0, isPlaying);
  }, [isPlaying, postTime, syncAudio]);

  const handleToggleAudio = useCallback(() => {
    const next = !audioEnabledRef.current;
    audioEnabledRef.current = next;
    setAudioEnabled(next);
    // Runs inside the click gesture so play() is allowed by autoplay policy.
    syncAudio(timeRef.current, isPlaying);
  }, [isPlaying, syncAudio]);

  const handleExportMp4 = useCallback(async () => {
    setExportState({ status: 'starting' });
    try {
      const response = await fetch('/api/render/hyperframes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          htmlDocument: effectiveHtmlDocument,
          durationSeconds,
          fps,
          width,
          height,
          variables,
          visualizationId,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to start HyperFrames render');
      }
      if (!data.taskId || !data.publicAccessToken) {
        throw new Error('HyperFrames export started without a trackable task');
      }

      setExportState({
        status: 'tracking',
        taskId: data.taskId,
        publicAccessToken: data.publicAccessToken,
      });
    } catch (err) {
      setExportState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Export failed',
      });
    }
  }, [durationSeconds, effectiveHtmlDocument, fps, height, title, variables, visualizationId, width]);

  // Subscribe to the durable export run. `useRealtimeRun` accepts an
  // undefined runId, so the hook stays mounted unconditionally and only
  // activates once an export is tracking.
  const exportTracking = exportState.status === 'tracking' ? exportState : null;
  const { run: exportRun, error: exportRunError } = useRealtimeRun<typeof renderHyperframesExportTask>(
    exportTracking?.taskId,
    {
      accessToken: exportTracking?.publicAccessToken,
      enabled: Boolean(exportTracking),
    },
  );
  // RenderJobView from the shared contract adapter (lib/render-job-view.ts)
  // — this component is the Phase 1 proof-consumer of the contract.
  const exportView = exportTracking
    ? hyperframesExportRunToView(exportTracking.taskId, exportRun, { subscribeError: exportRunError })
    : null;

  // Self-heal: if the export run EXPIRES in the queue before a worker attaches
  // AND the ~1h realtime token lapses, the subscription never observes the
  // terminal transition and the export card sticks on "Queued…" forever (the
  // same zombie class the narrated card had). When the realtime view is stuck
  // queued (or the subscription errored) we poll the server's reconciled status
  // — which asks the trigger API what actually happened — and swap to the
  // finished MP4 (completed) or the honest failure.
  const [reconciledExport, setReconciledExport] = useState<
    | { status: 'completed'; outputUrl?: string }
    | { status: 'failed'; error?: string }
    | null
  >(null);
  const exportTerminal = exportView
    ? exportView.status === 'completed' || exportView.status === 'failed' || exportView.status === 'canceled'
    : false;
  const exportStuckQueued =
    exportTracking != null && (!exportRun || exportView?.status === 'queued' || !!exportRunError);
  const exportTrackingTaskId = exportTracking?.taskId;
  useEffect(() => {
    if (!exportTrackingTaskId || reconciledExport || exportTerminal || !exportStuckQueued) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const res = await fetch(`/api/render/hyperframes/${encodeURIComponent(exportTrackingTaskId)}/status`);
        if (res.ok) {
          const data = (await res.json()) as { status?: string; error?: string; outputUrl?: string };
          if (!cancelled && data.status === 'completed') {
            setReconciledExport({ status: 'completed', outputUrl: data.outputUrl });
            return;
          }
          if (!cancelled && data.status === 'failed') {
            setReconciledExport({ status: 'failed', error: data.error });
            return;
          }
        }
      } catch {
        // transient — retry on the next tick
      }
      if (!cancelled) timer = setTimeout(poll, 20_000);
    };
    // Delay the first check so the realtime subscription gets first crack.
    timer = setTimeout(poll, 8_000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [exportTrackingTaskId, reconciledExport, exportTerminal, exportStuckQueued]);

  // A reconciled terminal state overrides the (stuck) realtime view everywhere
  // the card + header affordance read status from.
  const reconciledExportView: RenderJobView | null = reconciledExport
    ? reconciledExport.status === 'completed'
      ? {
          id: exportTrackingTaskId ?? 'hyperframes-export',
          kind: 'hyperframes-export',
          status: 'completed',
          progress: 1,
          outputUrl: reconciledExport.outputUrl,
          canCancel: false,
          canRetry: false,
        }
      : {
          id: exportTrackingTaskId ?? 'hyperframes-export',
          kind: 'hyperframes-export',
          status: 'failed',
          error: reconciledExport.error ?? 'The export run did not finish.',
          canCancel: false,
          canRetry: true,
        }
    : null;
  const effectiveExportView = reconciledExportView ?? exportView;

  // Tell the agent when the MP4 export finishes or fails — exactly like
  // PendingNarratedVideo does for the narrated pipeline. The bus dedups by
  // eventId, so re-renders are safe.
  const exportTaskId = exportTracking?.taskId;
  const exportViewStatus = effectiveExportView?.status;
  const exportViewUrl = effectiveExportView?.outputUrl;
  const exportViewError = effectiveExportView?.error;
  useEffect(() => {
    if (!exportTaskId) return;
    if (exportViewStatus === 'completed' && exportViewUrl) {
      emitAutoSystemMessage({
        eventId: `hyperframes-export-complete:${exportTaskId}`,
        kind: 'render-complete',
        payload: {
          taskId: exportTaskId,
          ...(visualizationId ? { visualizationId } : {}),
          videoUrl: exportViewUrl,
        },
        body: `HyperFrames MP4 export finished. The video is downloadable at ${exportViewUrl}.`,
      });
    }
    if (exportViewStatus === 'failed' || exportViewStatus === 'canceled') {
      emitAutoSystemMessage({
        eventId: `hyperframes-export-failed:${exportTaskId}`,
        kind: 'render-error',
        payload: {
          taskId: exportTaskId,
          ...(visualizationId ? { visualizationId } : {}),
          ...(exportViewError ? { error: exportViewError } : {}),
        },
        body: exportViewStatus === 'canceled'
          ? 'HyperFrames MP4 export was canceled before it started.'
          : `HyperFrames MP4 export failed: ${exportViewError ?? 'unknown error'}. The in-browser preview still works; offer to retry the export or move on.`,
      });
    }
  }, [exportTaskId, exportViewStatus, exportViewUrl, exportViewError, visualizationId]);

  const speedControls = (
    <div className="flex items-center gap-1">
      <Gauge weight="bold" className="mr-1 size-3.5 text-muted-foreground" />
      {SPEED_OPTIONS.map((option) => (
        <button
          key={option}
          onClick={() => setSpeed(option)}
          className={cn(
            'rounded px-1.5 py-0.5 text-xs font-medium transition-colors',
            speed === option
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          {option}x
        </button>
      ))}
    </div>
  );

  const player = (
    <div className="w-full bg-slate-950">
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: `${width} / ${height}` }}
      >
        <iframe
          ref={iframeRef}
          key={previewDocument}
          srcDoc={previewDocument}
          title={title}
          sandbox="allow-scripts"
          className="absolute inset-0 h-full w-full border-0 bg-transparent"
          onError={() => setRuntimeError('HyperFrames preview iframe failed to load')}
        />
        {/* Poster state — the composition itself, paused at frame 0, IS the
            poster; this overlay adds the scrim + play affordance until the
            first play gesture. */}
        {!hasStarted && (
          <button
            type="button"
            onClick={handleTogglePlay}
            aria-label="Play preview"
            className="group/poster absolute inset-0 flex items-center justify-center bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-slate-950/30 outline-none transition-colors hover:from-slate-950/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-400"
          >
            <span className="grid size-14 place-items-center rounded-full border border-white/20 bg-violet-500/90 text-white shadow-[0_8px_32px_rgba(139,92,246,0.45)] backdrop-blur-sm transition-transform group-hover/poster:scale-105">
              <Play weight="fill" className="ml-0.5 size-6" />
            </span>
            <span className="pointer-events-none absolute bottom-3 left-4 text-[10px] font-bold uppercase tracking-[2px] text-white/70">
              Preview · {formatTime(durationSeconds)} @ {fps}fps
            </span>
          </button>
        )}
      </div>
    </div>
  );

  const exportControl = (() => {
    if (exportState.status === 'starting') {
      return (
        <Button variant="ghost" size="sm" disabled>
          <Spinner weight="bold" className="size-4 animate-spin" />
          <span className="ml-1 text-xs">Starting</span>
        </Button>
      );
    }

    // Tracking a durable run — the shared RenderJobCard below the player owns
    // the full lifecycle chrome; the header keeps only a compact affordance.
    if (effectiveExportView?.status === 'queued' || effectiveExportView?.status === 'running') {
      return (
        <Button variant="ghost" size="sm" disabled title={effectiveExportView.stageLabel}>
          <Spinner weight="bold" className="size-4 animate-spin" />
          <span className="ml-1 text-xs">
            {typeof effectiveExportView.progress === 'number' ? `${Math.round(effectiveExportView.progress * 100)}%` : 'Exporting'}
          </span>
        </Button>
      );
    }

    if (effectiveExportView?.status === 'completed' && effectiveExportView.outputUrl) {
      return (
        <Button variant="ghost" size="sm" asChild>
          <a href={effectiveExportView.outputUrl} target="_blank" rel="noopener noreferrer" download>
            <Download weight="bold" className="size-4" />
            <span className="ml-1 text-xs">Download MP4</span>
          </a>
        </Button>
      );
    }

    // idle / start-error / failed / canceled — Export (again) is the affordance;
    // failure detail + Retry live in the card.
    return (
      <Button variant="ghost" size="sm" onClick={handleExportMp4} title="Render with HyperFrames Lambda">
        <Download weight="bold" className="size-4" />
        <span className="ml-1 text-xs">Export MP4</span>
      </Button>
    );
  })();

  // The shared card consumes ONLY the contract view. A pre-task start failure
  // (the POST itself failed) is synthesized into the same shape so the card
  // stays the single chrome for every export state.
  const exportCardView: RenderJobView | null = exportState.status === 'error'
    ? {
        id: 'hyperframes-export-start',
        kind: 'hyperframes-export',
        status: 'failed',
        error: exportState.message,
        canCancel: false,
        canRetry: true,
      }
    : effectiveExportView;

  const exportStatus = exportCardView ? (
    <div className="border-t border-border/60 bg-background/60 px-3 py-2.5">
      <RenderJobCard view={exportCardView} onRetry={handleExportMp4} />
      {(exportCardView.status === 'queued' || exportCardView.status === 'running') && (
        <p className="mt-1.5 px-1 text-[10px] text-muted-foreground/70">
          The export keeps running even if you navigate away.
        </p>
      )}
    </div>
  ) : null;

  const scrubPct = durationSeconds > 0
    ? Math.max(0, Math.min(100, (currentTime / durationSeconds) * 100))
    : 0;

  const controls = (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 bg-background/80 px-3 py-2 backdrop-blur-md">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleTogglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause weight="bold" className="size-4" /> : <Play weight="bold" className="size-4" />}
        </Button>
        {/* Scrub bar — synced to the GSAP timeline clock; drag to seek. */}
        <input
          aria-label="Timeline"
          type="range"
          min={0}
          max={durationSeconds}
          step={1 / fps}
          value={currentTime}
          onChange={(event) => handleSeek(Number(event.target.value))}
          style={{
            background: `linear-gradient(to right, rgb(139 92 246) ${scrubPct}%, color-mix(in srgb, currentColor 18%, transparent) ${scrubPct}%)`,
          }}
          className={cn(
            'h-1.5 min-w-0 flex-1 cursor-pointer appearance-none rounded-full text-muted-foreground outline-none',
            'focus-visible:ring-2 focus-visible:ring-violet-400/60',
            '[&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0_0_3px_rgba(139,92,246,0.35)] [&::-webkit-slider-thumb]:transition-transform hover:[&::-webkit-slider-thumb]:scale-110',
            '[&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-[0_0_0_3px_rgba(139,92,246,0.35)]',
          )}
        />
        <span className="w-[88px] shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-foreground">
          {formatTime(currentTime)} / {formatTime(durationSeconds)}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {speedControls}
        {(audioTracks?.length ?? 0) > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleAudio}
            title={audioEnabled ? 'Mute audio' : 'Unmute audio'}
            aria-label={audioEnabled ? 'Mute audio' : 'Unmute audio'}
          >
            {audioEnabled
              ? <SpeakerHigh weight="bold" className="size-4" />
              : <SpeakerSlash weight="bold" className="size-4 text-muted-foreground" />}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={handleRestart} title="Restart" aria-label="Restart">
          <ArrowCounterClockwise weight="bold" className="size-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <div
        className={cn(
          'group relative w-full overflow-hidden',
          displayContext === 'artifact'
            ? 'rounded-none border-0 bg-transparent'
            : 'rounded-lg border bg-card',
        )}
      >
        <div
          className={cn(
            'flex items-center justify-between gap-3 px-4 py-2',
            displayContext === 'artifact' ? '' : 'border-b bg-muted/30',
          )}
        >
          {displayContext === 'artifact' ? (
            <div className="min-w-0" />
          ) : (
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-medium">{title}</h3>
              {description && <p className="truncate text-xs text-muted-foreground">{description}</p>}
            </div>
          )}
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs tabular-nums text-muted-foreground">
              {formatTime(durationSeconds)} @ {fps}fps
            </span>
            <div className="flex items-center gap-2 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
              {renderActions}
              {exportControl}
              <Button variant="ghost" size="sm" onClick={() => setIsFullscreen(true)}>
                <ArrowsOut weight="bold" className="size-4" />
                <span className="ml-1 hidden text-xs md:inline">Fullscreen</span>
              </Button>
            </div>
          </div>
        </div>
        {player}
        {exportStatus}
        {controls}
      </div>

      {isFullscreen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-slate-950">
          <div className="flex items-center justify-between border-b border-slate-800 bg-slate-950/95 px-6 py-4">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-white">{title}</h2>
              {description && <p className="truncate text-sm text-slate-400">{description}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {speedControls}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFullscreen(false)}
                className="border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
              >
                <X weight="bold" className="mr-1 size-4" />
                Close
                <span className="ml-2 text-xs text-slate-400">(Esc)</span>
              </Button>
            </div>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center p-6">
            <div className="w-full max-w-6xl">
              {player}
              {exportStatus}
              {controls}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function HyperframesToolOutput({ output, visualizationId, displayContext = 'inline', renderActions }: {
  output: {
    success: boolean;
    title?: string;
    description?: string;
    htmlDocument?: string;
    durationSeconds?: number;
    fps?: number;
    width?: number;
    height?: number;
    variables?: Record<string, unknown>;
    audioTracks?: HyperframesAudioTrackInput[];
    error?: string;
  };
  visualizationId?: string;
  displayContext?: 'inline' | 'artifact';
  renderActions?: React.ReactNode;
}) {
  if (!output.success) {
    return (
      <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4">
        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">HyperFrames Error</p>
        <pre className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">{output.error}</pre>
      </div>
    );
  }

  if (!output.htmlDocument) return null;

  return (
    <HyperframesRenderer
      title={output.title || 'HyperFrames Video'}
      description={output.description}
      htmlDocument={output.htmlDocument}
      durationSeconds={output.durationSeconds || 8}
      fps={output.fps || 30}
      width={output.width || 1280}
      height={output.height || 720}
      variables={output.variables}
      audioTracks={output.audioTracks}
      visualizationId={visualizationId}
      displayContext={displayContext}
      renderActions={renderActions}
    />
  );
}
